import {
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
  Tooltip,
} from '@ads/components-react';
import {
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  LockClosedIcon,
  LockOpenIcon,
  PaperClipIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PersonGridRepositoryResponse } from '@/shared/api/generated/types/PersonGridRepositoryResponse';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface PersonTableProps {
  readonly data: readonly PersonGridRepositoryResponse[];
  readonly isLoading: boolean;
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
  readonly onEdit: (id: number) => void;
  readonly onPayments: (id: number) => void;
  readonly onEnrollments: (id: number) => void;
  readonly onAttachments: (id: number) => void;
  readonly onResume: (id: number) => void;
  readonly onActivate: (id: number) => void;
  readonly onDeactivate: (id: number) => void;
  readonly onDelete: (id: number) => void;
}

export function PersonTable({
  data,
  isLoading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  onEdit,
  onPayments,
  onEnrollments,
  onAttachments,
  onResume,
  onActivate,
  onDeactivate,
  onDelete,
}: PersonTableProps) {
  const { t } = useTranslation('personManagement');

  const columns = useMemo<ColumnDef<PersonGridRepositoryResponse, unknown>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('table.columns.code'),
        size: 88,
        cell: info => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {info.getValue() as string}
          </span>
        ),
      },
      {
        id: 'name',
        header: t('table.columns.name'),
        cell: ({ row }) => {
          const value = `${row.original.firstName ?? ''} ${row.original.lastName ?? ''}`.trim();
          return (
            <Tooltip title={value} side="top" align="start">
              <span className="block truncate ts-paragraph-xxs text-ctx-content-base">{value}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'email',
        header: t('table.columns.email'),
        cell: info => {
          const value = (info.getValue() as string) ?? '';
          return (
            <Tooltip title={value} side="top" align="start">
              <span className="block truncate ts-paragraph-xxs text-ctx-content-base">{value}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'cpf',
        header: t('table.columns.cpf'),
        size: 150,
        cell: info => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {info.getValue() as string}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('table.columns.status'),
        size: 116,
        cell: ({ row }) => (
          <Tag
            label={row.original.active ? t('status.active') : t('status.inactive')}
            size="md"
            status={row.original.active ? 'positive' : 'default'}
            custom={{ text: 'text-nowrap' }}
          />
        ),
      },
      {
        id: 'actions',
        header: t('table.columns.actions'),
        size: 80,
        cell: ({ row }) => {
          const id = row.original.id ?? 0;
          const isActive = row.original.active ?? true;
          return (
            <div className="flex justify-end">
              <Dropdown
                trigger={
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    ariaLabel={t('table.columns.actions')}
                    hierarchy="tertiary"
                    size="md"
                  />
                }
                align="end"
              >
                <DropdownItem onClick={() => onEdit(id)}>
                  <DropdownItemContent leadingIcon={PencilSquareIcon}>
                    {t('actions.edit')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onResume(id)}>
                  <DropdownItemContent leadingIcon={EyeIcon}>
                    {t('actions.resume')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onEnrollments(id)}>
                  <DropdownItemContent leadingIcon={ClipboardDocumentListIcon}>
                    {t('actions.enrollments')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onPayments(id)}>
                  <DropdownItemContent leadingIcon={CurrencyDollarIcon}>
                    {t('actions.payments')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onAttachments(id)}>
                  <DropdownItemContent leadingIcon={PaperClipIcon}>
                    {t('actions.attachments')}
                  </DropdownItemContent>
                </DropdownItem>
                {isActive ? (
                  <DropdownItem onClick={() => onDeactivate(id)}>
                    <DropdownItemContent leadingIcon={LockClosedIcon}>
                      {t('actions.deactivate')}
                    </DropdownItemContent>
                  </DropdownItem>
                ) : (
                  <DropdownItem onClick={() => onActivate(id)}>
                    <DropdownItemContent leadingIcon={LockOpenIcon}>
                      {t('actions.activate')}
                    </DropdownItemContent>
                  </DropdownItem>
                )}
                <DropdownItem status="destructive" onClick={() => onDelete(id)}>
                  <DropdownItemContent leadingIcon={TrashIcon}>
                    {t('actions.delete')}
                  </DropdownItemContent>
                </DropdownItem>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [
      t,
      onEdit,
      onPayments,
      onEnrollments,
      onAttachments,
      onResume,
      onActivate,
      onDeactivate,
      onDelete,
    ],
  );

  return (
    <DataTable
      data={data as PersonGridRepositoryResponse[]}
      columns={columns}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      tableFixed
    />
  );
}
